export interface IParsedCoordinates {
	lat: number
	lng: number
}

const isInRange = (lat: number, lng: number): boolean => {
	return Number.isFinite(lat) && Number.isFinite(lng)
		&& Math.abs(lat) <= 90
		&& Math.abs(lng) <= 180
}

const applyHemisphere = (value: number, hemisphere?: string): number => {
	if (!hemisphere) {
		return value
	}
	const letter = hemisphere.toUpperCase()
	return letter === 'S' || letter === 'W' ? -Math.abs(value) : Math.abs(value)
}

/**
 * Recognises coordinates pasted from Google Maps, so the map search accepts them next to a
 * plain address. Handles the three shapes Google actually produces:
 *
 *   49.839683, 24.029717            - "copy coordinates" on a right click
 *   .../maps/@49.839683,24.029717,15z   - the URL of the current view
 *   ...!3d49.839683!4d24.029717     - the URL of an opened place
 *
 * Degree signs and N/S/E/W letters are tolerated. Degrees-minutes-seconds are not supported -
 * Google copies decimals.
 */
export const parseCoordinates = (input: string): IParsedCoordinates | null => {
	const value = (input || '').trim()

	if (!value) {
		return null
	}

	// the place pin comes first: a link to a place carries both the pin and the view centre,
	// and the pin is what the user meant to share
	const fromPlace = value.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/)
	if (fromPlace) {
		const lat = Number(fromPlace[1])
		const lng = Number(fromPlace[2])
		if (isInRange(lat, lng)) {
			return {lat, lng}
		}
	}

	const fromView = value.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/)
	if (fromView) {
		const lat = Number(fromView[1])
		const lng = Number(fromView[2])
		if (isInRange(lat, lng)) {
			return {lat, lng}
		}
	}

	const fromPair = value.match(
		/^(-?\d+(?:[.,]\d+)?)\s*°?\s*([NnSs])?\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)\s*°?\s*([EeWw])?$/
	)
	if (fromPair) {
		const lat = applyHemisphere(Number(fromPair[1].replace(',', '.')), fromPair[2])
		const lng = applyHemisphere(Number(fromPair[3].replace(',', '.')), fromPair[4])
		if (isInRange(lat, lng)) {
			return {lat, lng}
		}
	}

	return null
}
